import { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Chip,
  Fade,
  Grid,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Add as AddIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import {
  getCoordinatorsAndMonitors,
  createCoordinatorOrMonitor,
  getInstitutions,
  updateSimuladorUrl,
  getSimuladorUrl,
} from '../services/api.js';

const StyledTableCell = styled(TableCell)(() => ({
  fontSize: '0.9rem',
  fontWeight: 600,
  color: '#344767',
  borderBottom: '1px solid #f0f2f5',
  padding: '16px',
}));

const StyledTableRow = styled(TableRow)(() => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#ffffff',
  },
  '&:nth-of-type(even)': {
    backgroundColor: '#f8f9fa',
  },
  '&:hover': {
    backgroundColor: '#f1f3f9 !important',
    transition: 'background-color 0.2s ease',
  },
  '& td, & th': {
    border: 0,
  },
  cursor: 'pointer',
}));

const AdminUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [simuladorUrl, setSimuladorUrl] = useState('');
  const [isSubmittingUrl, setIsSubmittingUrl] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'COR',
    institution: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchInstitutions();
    fetchSimuladorUrl();
  }, []);

  const fetchSimuladorUrl = async () => {
    try {
      const data = await getSimuladorUrl();
      if (data && data.url) {
        setSimuladorUrl(data.url);
      }
    } catch {
      // Falha silenciosa ou log alternativo
    }
  };

  const fetchInstitutions = async () => {
    try {
      const data = await getInstitutions();
      setInstitutions(data);
      setError('');
    } catch (err) {
      setError(`Erro ao carregar instituicoes: ${err.message}`);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await getCoordinatorsAndMonitors();
      setUsers(data);
      setError('');
    } catch (err) {
      setError(err.message || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'CORD',
      institution: '',
    });
    setError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'CORD',
      institution: '',
    });
    setError('');
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Nome é obrigatório');
      return false;
    }
    if (!formData.email.trim()) {
      setError('E-mail é obrigatório');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('E-mail inválido');
      return false;
    }
    if (!formData.password || formData.password.length < 6) {
      setError('Senha deve ter pelo menos 6 caracteres');
      return false;
    }
    if (!formData.role) {
      setError('Função é obrigatória');
      return false;
    }
    if (!formData.institution) {
      setError('Instituição é obrigatória');
      return false;
    }
    return true;
  };

  const handleCreateUser = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      await createCoordinatorOrMonitor(formData);
      setSuccess('Usuário criado com sucesso!');
      handleCloseDialog();
      await fetchUsers();
    } catch (err) {
      setError(err.message || 'Erro ao criar usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSimuladorUrl = async () => {
    if (!simuladorUrl.trim()) {
      setError('A URL do simulador não pode estar vazia');
      return;
    }

    try {
      setIsSubmittingUrl(true);
      setError('');
      await updateSimuladorUrl(simuladorUrl);
      setSuccess('URL do simulador atualizada com sucesso!');
      setSimuladorUrl('');
    } catch (err) {
      setError(err.message || 'Erro ao atualizar URL do simulador');
    } finally {
      setIsSubmittingUrl(false);
    }
  };

  const handleCloseSnackbar = () => {
    setError('');
    setSuccess('');
  };

  const getRoleLabel = role => {
    const roleMap = {
      CORD: 'Coordenador',
      MON: 'Monitor',
      ADM: 'Administrador',
      USR: 'Usuário',
    };
    const roleCode = role?.replace('ROLE_', '') || role;
    return roleMap[roleCode] || role || 'Desconhecido';
  };

  const getInstitutionName = id => {
    const inst = institutions.find(i => i.id_instituicao === id);
    return inst ? inst.nm_instituicao : 'N/A';
  };

  if (loading && users.length === 0) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='60vh'
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Fade in={true} timeout={800}>
      <Box sx={{ p: { xs: 0, md: 1 } }}>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          mb={4}
          sx={{
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant='h4'
              fontWeight='800'
              sx={{ color: '#003B46', letterSpacing: '-0.5px' }}
            >
              Gestão de Acessos
            </Typography>
            <Typography variant='body2' sx={{ color: '#67748e', mt: 0.5 }}>
              Administre coordenadores, monitores e configurações do sistema
            </Typography>
          </Box>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            sx={{
              bgcolor: '#003B46',
              borderRadius: '12px',
              px: 3,
              py: 1.2,
              textTransform: 'none',
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(0, 59, 70, 0.2)',
              '&:hover': {
                bgcolor: '#00262d',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 15px rgba(0, 59, 70, 0.3)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Criar Usuário
          </Button>
        </Box>

        <Grid container spacing={3} sx={{ flexDirection: 'column' }}>
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: '20px',
                border: '1px solid #e0e4e7',
                bgcolor: '#ffffff',
                height: '100%',
              }}
            >
              <Box display='flex' alignItems='center' gap={1.5} mb={3}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: '10px',
                    bgcolor: '#E0F2F1',
                    color: '#003B46',
                    display: 'flex',
                  }}
                >
                  <SettingsIcon />
                </Box>
                <Typography
                  variant='h6'
                  fontWeight='bold'
                  sx={{ color: '#003B46' }}
                >
                  Configurações
                </Typography>
              </Box>

              <Typography variant='body2' sx={{ color: '#67748e', mb: 3 }}>
                Defina a URL base que será utilizada para o acesso ao simulador
                da trilha.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label='URL do Simulador'
                  variant='outlined'
                  value={simuladorUrl}
                  onChange={e => setSimuladorUrl(e.target.value)}
                  placeholder='https://simulador.exemplo.com'
                  disabled={isSubmittingUrl}
                  helperText={
                    simuladorUrl
                      ? `URL Atual: ${simuladorUrl}`
                      : 'Nenhuma URL configurada'
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                    },
                  }}
                />
                <Button
                  variant='contained'
                  fullWidth
                  onClick={handleUpdateSimuladorUrl}
                  disabled={isSubmittingUrl}
                  sx={{
                    bgcolor: '#00BCD4',
                    borderRadius: '12px',
                    py: 1.5,
                    fontWeight: 'bold',
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: '#00ACC1',
                    },
                  }}
                >
                  {isSubmittingUrl ? (
                    <CircularProgress size={24} color='inherit' />
                  ) : (
                    'Atualizar URL'
                  )}
                </Button>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: '20px',
                border: '1px solid #e0e4e7',
                overflow: 'hidden',
                bgcolor: '#ffffff',
              }}
            >
              <Box sx={{ p: 3, borderBottom: '1px solid #f0f2f5' }}>
                <Typography
                  variant='h6'
                  fontWeight='bold'
                  sx={{ color: '#003B46' }}
                >
                  Coordenadores e Monitores
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                    <TableRow>
                      <StyledTableCell>Usuário</StyledTableCell>
                      <StyledTableCell>E-mail</StyledTableCell>
                      <StyledTableCell>Instituição</StyledTableCell>
                      <StyledTableCell>Função</StyledTableCell>
                      <StyledTableCell align='center'>Status</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align='center' sx={{ py: 8 }}>
                          <Typography variant='body1' color='text.secondary'>
                            Nenhum usuário encontrado
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map(user => (
                        <StyledTableRow key={user.id_usuario || user.id}>
                          <StyledTableCell>
                            <Box display='flex' alignItems='center' gap={2}>
                              <Avatar
                                sx={{
                                  bgcolor: '#00BCD4',
                                  width: 36,
                                  height: 36,
                                  fontSize: '0.9rem',
                                  fontWeight: 'bold',
                                }}
                              >
                                {user.nm_usuario?.charAt(0) || 'U'}
                              </Avatar>
                              <Typography variant='body2' fontWeight='700'>
                                {user.nm_usuario || user.name || 'N/A'}
                              </Typography>
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell sx={{ color: '#67748e' }}>
                            {user.nm_email || user.email}
                          </StyledTableCell>
                          <StyledTableCell
                            sx={{ color: '#67748e', fontSize: '0.85rem' }}
                          >
                            {getInstitutionName(user.id_instituicao)}
                          </StyledTableCell>
                          <StyledTableCell>
                            <Chip
                              label={getRoleLabel(
                                user.cd_role ||
                                  user.role ||
                                  `ROLE_${user.id_role}`
                              )}
                              size='small'
                              sx={{
                                fontWeight: 'bold',
                                fontSize: '0.75rem',
                                bgcolor: '#f0f2f5',
                                color: '#344767',
                              }}
                            />
                          </StyledTableCell>
                          <StyledTableCell align='center'>
                            <Chip
                              label={
                                user.bo_liberado !== false
                                  ? 'Ativo'
                                  : 'Pendente'
                              }
                              size='small'
                              color={
                                user.bo_liberado !== false
                                  ? 'success'
                                  : 'default'
                              }
                              sx={{
                                fontWeight: 'bold',
                                fontSize: '0.75rem',
                                minWidth: '80px',
                              }}
                            />
                          </StyledTableCell>
                        </StyledTableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth='sm'
          fullWidth
          PaperProps={{
            sx: { borderRadius: '20px', p: 1 },
          }}
        >
          <DialogTitle sx={{ fontWeight: 'bold', color: '#003B46' }}>
            <Box display='flex' alignItems='center' gap={1.5}>
              <Avatar sx={{ bgcolor: '#003B46', width: 32, height: 32 }}>
                <PersonIcon sx={{ fontSize: 20 }} />
              </Avatar>
              Criar Novo Usuário
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 2 }}
            >
              <TextField
                autoFocus
                id='name'
                name='name'
                label='Nome Completo'
                type='text'
                fullWidth
                variant='outlined'
                value={formData.name}
                onChange={handleChange}
                required
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
              <TextField
                id='email'
                name='email'
                label='E-mail Profissional'
                type='email'
                fullWidth
                variant='outlined'
                value={formData.email}
                onChange={handleChange}
                required
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
              <TextField
                id='password'
                name='password'
                label='Senha de Acesso'
                type='password'
                fullWidth
                variant='outlined'
                value={formData.password}
                onChange={handleChange}
                required
                helperText='Mínimo de 6 caracteres'
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
              <FormControl fullWidth>
                <InputLabel id='institution-label'>Instituição</InputLabel>
                <Select
                  labelId='institution-label'
                  id='institution'
                  name='institution'
                  value={formData.institution}
                  label='Instituição'
                  onChange={handleChange}
                  required
                  sx={{ borderRadius: '12px' }}
                >
                  {institutions.map(inst => (
                    <MenuItem
                      key={inst.id_instituicao}
                      value={inst.id_instituicao}
                    >
                      {inst.nm_instituicao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id='role-label'>Função do Usuário</InputLabel>
                <Select
                  labelId='role-label'
                  id='role'
                  name='role'
                  value={formData.role}
                  label='Função do Usuário'
                  onChange={handleChange}
                  sx={{ borderRadius: '12px' }}
                >
                  <MenuItem value='COR'>Coordenador</MenuItem>
                  <MenuItem value='MON'>Monitor</MenuItem>
                </Select>
              </FormControl>
              {error && (
                <Alert
                  severity='error'
                  sx={{
                    borderRadius: '12px',
                    border: '1px solid currentColor',
                  }}
                >
                  {error}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button
              onClick={handleCloseDialog}
              sx={{
                color: '#67748e',
                textTransform: 'none',
                fontWeight: 'bold',
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateUser}
              color='primary'
              variant='contained'
              disabled={loading}
              sx={{
                bgcolor: '#003B46',
                borderRadius: '12px',
                px: 4,
                py: 1,
                textTransform: 'none',
                fontWeight: 'bold',
                '&:hover': { bgcolor: '#00262d' },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Salvar Usuário'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={!!error || !!success}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={error ? 'error' : 'success'}
            sx={{ width: '100%' }}
          >
            {error || success}
          </Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
};

export default AdminUserManagement;
